import React, { useState, useEffect } from 'react';
import { sponsorsAPI } from '../services/api';
import { Handshake, Loader2, Building } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { usePageContent } from '../hooks/usePageContent';
import { usePageTitle } from '../hooks/usePageTitle';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  if (url.startsWith('/api')) return `${API_BASE_URL}${url}`;
  return `${API_BASE_URL}/api/uploads/image/${url}`;
};

const Sponsors = () => {
  const [sponsors, setSponsors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { t, language } = useLanguage();
  const { getContent } = usePageContent('sponsors');
  
  // Dynamic page title
  usePageTitle('nav.sponsors', 'Sponsors', 'Sponsorlar');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const sponsorsData = await sponsorsAPI.getAll();
        setSponsors(sponsorsData);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center" style={{ background: '#0F172A' }}>
        <Loader2 className="w-12 h-12 animate-spin" style={{ color: '#00F0FF' }} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center" style={{ background: '#0F172A' }}>
        <div className="text-center">
          <p className="body-large mb-4" style={{ color: '#F8FAFC' }}>{t('sponsors.error.loadFailed')}</p>
          <p className="body-small" style={{ color: '#94A3B8' }}>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20" style={{ background: '#0F172A' }} data-testid="sponsors-page">
      {/* Hero Section */}
      <section className="relative py-24 px-[7.6923%] overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('${getContent('hero_image') || 'https://images.unsplash.com/photo-1521737604893-d14cc237f11d?w=1920'}')`
          }}
        >
          <div className="absolute inset-0" style={{
            background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.9) 0%, rgba(30, 41, 59, 0.85) 50%, rgba(0, 240, 255, 0.2) 100%)'
          }}></div>
        </div>
        <div className="relative z-10 max-w-[1400px] mx-auto text-center">
          <h1 className="display-huge mb-6" style={{
            background: 'linear-gradient(90deg, #00F0FF 0%, #D8B4FE 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}>{getContent('hero_title', t, 'sponsors.hero.title', language)}</h1>
          <p className="body-large max-w-3xl mx-auto" style={{ color: '#F8FAFC', opacity: 0.9 }}>
            {getContent('hero_subtitle', t, 'sponsors.hero.description', language)}
          </p>
        </div>
      </section>

      {/* Sponsors Grid */}
      <section className="py-24 px-[7.6923%]" style={{ background: '#0F172A' }}>
        <div className="max-w-[1400px] mx-auto">
          <h2 className="display-medium mb-12 text-center" style={{
            background: 'linear-gradient(90deg, #00F0FF 0%, #D8B4FE 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}>{getContent('partners_title', t, 'sponsors.partnersTitle', language)}</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {sponsors.map((sponsor) => (
              <div key={sponsor.id} className="glass-card p-8 flex flex-col items-center justify-center min-h-[200px] dark-transition dark-hover"
                data-testid={`sponsor-card-${sponsor.id}`}>
                <div className="h-24 w-full flex items-center justify-center mb-4 overflow-hidden">
                  {sponsor.logo ? (
                    <img 
                      src={getImageUrl(sponsor.logo)} 
                      alt={sponsor.name} 
                      className="max-w-full max-h-full object-contain"
                    />
                  ) : (
                    <Building size={48} style={{ color: '#64748B' }} />
                  )}
                </div>
                <p className="text-center font-medium mb-2" style={{ color: '#F8FAFC' }}>{sponsor.name}</p>
                {sponsor.tier && sponsor.tier !== 'standard' && (
                  <span className="body-small uppercase tracking-wider" style={{
                    color: sponsor.tier === 'gold' ? '#FFD700' : 
                           sponsor.tier === 'silver' ? '#C0C0C0' : 
                           sponsor.tier === 'bronze' ? '#CD7F32' : '#00F0FF',
                    textShadow: `0 0 10px ${
                      sponsor.tier === 'gold' ? 'rgba(255, 215, 0, 0.5)' : 
                      sponsor.tier === 'silver' ? 'rgba(192, 192, 192, 0.5)' : 
                      sponsor.tier === 'bronze' ? 'rgba(205, 127, 50, 0.5)' : 'rgba(0, 240, 255, 0.5)'
                    }`
                  }}>{t(`sponsors.tiers.${sponsor.tier}`)}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Become a Sponsor */}
      <section className="py-24 px-[7.6923%]" style={{
        background: 'rgba(30, 41, 59, 0.4)',
        backdropFilter: 'blur(8px)'
      }}>
        <div className="max-w-[1400px] mx-auto">
          <div className="max-w-3xl mx-auto text-center">
            <div className="w-20 h-20 flex items-center justify-center mx-auto mb-8" style={{
              background: 'rgba(0, 240, 255, 0.15)',
              boxShadow: '0 0 20px rgba(0, 240, 255, 0.3)'
            }}>
              <Handshake size={40} style={{ color: '#00F0FF' }} />
            </div>
            <h2 className="display-medium mb-6" style={{ color: '#F8FAFC' }}>
              {getContent('cta_title', t, 'sponsors.cta.title', language)}
            </h2>
            <p className="body-large mb-8 leading-relaxed" style={{ color: '#F8FAFC', opacity: 0.9 }}>
              {getContent('cta_description', t, 'sponsors.cta.description', language)}
            </p>
            
            <div className="glass-card p-8 mb-8 text-left">
              <h3 className="heading-2 mb-4" style={{ color: '#00F0FF', textShadow: '0 0 10px rgba(0, 240, 255, 0.4)' }}>
                {getContent('benefits_title', t, 'sponsors.benefits.title', language)}
              </h3>
              <ul className="space-y-3">
                <li className="body-medium flex items-start gap-3" style={{ color: '#F8FAFC', opacity: 0.9 }}>
                  <span style={{ color: '#00F0FF', fontWeight: 'bold' }}>•</span>
                  <span>{getContent('benefits_item1', t, 'sponsors.benefits.item1', language)}</span>
                </li>
                <li className="body-medium flex items-start gap-3" style={{ color: '#F8FAFC', opacity: 0.9 }}>
                  <span style={{ color: '#00F0FF', fontWeight: 'bold' }}>•</span>
                  <span>{getContent('benefits_item2', t, 'sponsors.benefits.item2', language)}</span>
                </li>
                <li className="body-medium flex items-start gap-3" style={{ color: '#F8FAFC', opacity: 0.9 }}>
                  <span style={{ color: '#00F0FF', fontWeight: 'bold' }}>•</span>
                  <span>{getContent('benefits_item3', t, 'sponsors.benefits.item3', language)}</span>
                </li>
                <li className="body-medium flex items-start gap-3" style={{ color: '#F8FAFC', opacity: 0.9 }}>
                  <span style={{ color: '#00F0FF', fontWeight: 'bold' }}>•</span>
                  <span>{getContent('benefits_item4', t, 'sponsors.benefits.item4', language)}</span>
                </li>
              </ul>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="mailto:kartalair.sponsorluk@gmail.com?subject=Sponsorship Inquiry"
                className="btn-primary inline-flex justify-center" data-testid="sponsor-email-btn">
                {getContent('cta_button', t, 'sponsors.cta.button', language)}
              </a>
              <a href="/contact" className="btn-secondary inline-flex justify-center">
                {t('common.learnMore')}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Support Impact */}
      <section className="py-24 px-[7.6923%]" style={{ background: '#0F172A' }}>
        <div className="max-w-[1400px] mx-auto">
          <h2 className="display-medium mb-12 text-center" style={{ color: '#F8FAFC' }}>
            {getContent('impact_title', t, 'sponsors.impact.title', language)}
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="glass-card p-8 text-center dark-transition dark-hover">
              <p className="display-large mb-4" style={{ color: '#00F0FF', textShadow: '0 0 15px rgba(0, 240, 255, 0.5)' }}>$15K+</p>
              <p className="body-medium mb-2" style={{ color: '#F8FAFC' }}>{getContent('impact_budget', t, 'sponsors.impact.budget', language)}</p>
              <p className="body-small" style={{ color: '#F8FAFC', opacity: 0.85 }}>
                {getContent('impact_budget_desc', t, 'sponsors.impact.budgetDesc', language)}
              </p>
            </div>
            <div className="glass-card p-8 text-center dark-transition dark-hover">
              <p className="display-large mb-4" style={{ color: '#00F0FF', textShadow: '0 0 15px rgba(0, 240, 255, 0.5)' }}>1000+</p>
              <p className="body-medium mb-2" style={{ color: '#F8FAFC' }}>{getContent('impact_hours', t, 'sponsors.impact.hours', language)}</p>
              <p className="body-small" style={{ color: '#F8FAFC', opacity: 0.85 }}>
                {getContent('impact_hours_desc', t, 'sponsors.impact.hoursDesc', language)}
              </p>
            </div>
            <div className="glass-card p-8 text-center dark-transition dark-hover">
              <p className="display-large mb-4" style={{ color: '#00F0FF', textShadow: '0 0 15px rgba(0, 240, 255, 0.5)' }}>{sponsors.length > 0 ? sponsors.length : '4'}</p>
              <p className="body-medium mb-2" style={{ color: '#F8FAFC' }}>{getContent('impact_active_sponsors', t, 'sponsors.impact.activeSponsors', language)}</p>
              <p className="body-small" style={{ color: '#F8FAFC', opacity: 0.85 }}>
                {getContent('impact_active_sponsors_desc', t, 'sponsors.impact.activeSponsorsDesc', language)}
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Sponsors;
